import React, { useEffect, useMemo } from 'react'
import { FieldArray, Form, withFormik, FormikProps } from 'formik'
import { get } from 'lodash-es'
import * as yup from 'yup'
import { Button, Divider, Grid, InfoBox, Segment } from '../../../../components'
import InvoicePanel from './InvoicePanel/InvoicePanel'
import styles from './InvoicesForm.module.scss'
import { leaseTypes } from 'modules/api/portfolio/constants'
import RouterPromptModal from '../../Modals/RouterPromptModal/RouterPromptModal'
import SvgInfo from 'views/components/atoms/Icons/Controls/Info'

interface InvoicesFormProps {
  agencyId: string
  ownerId: string
  agencyName: string
  invoiceParties: Array<{
    value: string
    label: string
    tag: string
  }>
  currentPortfolioId: string
  onSubmit?: (values: any) => void
  leaseType?: string
  initialValues?: object
  emptyInvoice?: object
  fetchInvoiceTypes?: () => void
  monthlyInvoiceTypes: Array<{
    value: string
    label: string
    [key: string]: any
  }>
  onceOffInvoiceTypes: Array<{
    value: string
    label: string
    [key: string]: any
  }>
  getInvoiceTypeNameByValue: (value: string) => string
  defaultBeneficiaries: Array<Array<any>>
  handleInvoiceTemplateChanged: (id: string) => void
  handleInvoiceTemplateRemoved: (id: string) => void
  getPartyTagsById: (partyId: string) => string[]
  fetchPartyTags?: () => void
  /** List of party tags for selection */
  partyTags: Array<{
    value: string
    label: string
  }>
  easyPayData: object
  fetchEasyPayData: (reference: string) => void
  agencyBankDetails?: object
  isDraftPortfolio?: boolean
  propertyOwnerName: string
  propertyOwnerBankDetails?: object
  getPartyBankDetails: (id: string) => any
  redirect?: (path: string) => void
  selectedTenant?: string | null
  isReadOnly?: boolean
  getPartyNameById: (id: string) => string
  getInvoiceTypeByValue: (value: string) => any
  isCurrentAgencyGlobalVatEnabled?: boolean
}

interface FormValues {
  invoices: any[]
}

type InvoicesFormWithFormikProps = InvoicesFormProps & FormikProps<FormValues>

const clearanceSchema = yup.object({
  type: yup.string(),
  value: yup.object({
    grossAmount: yup.string(), // string for currency formatting
    vat: yup.boolean(),
  }),
})

const validationSchema = yup.object().shape({
  invoices: yup.array(
    yup.object().shape({
      autoDeliver: yup.boolean(),
      category: yup.string().required('required'),
      invoicePartyId: yup.string().required('required'),
      interval: yup.string().required('required.'),
      netAmount: yup.string(),
      vatable: yup.boolean(),
      paymentRules: yup
        .array()
        .of(
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Lazy<OptionalObjectSchema<{ bene... Remove this comment to see the full error message
          yup.lazy(function (pr) {
            if (!pr?.beneficiary?.type) {
              return yup.object({
                beneficiary: yup.object({
                  type: yup.string(),
                  value: yup.object(),
                }),
              })
            }
            return pr.beneficiary.type === 'EasyPayBeneficiary'
              ? yup.object({
                  beneficiary: yup.object({
                    type: yup.string(),
                    value: yup.object({
                      beneficiaryId: yup.string(),
                      beneficiaryTag: yup.string(),
                      easyPayReference: yup.string(),
                      amount: yup.string(), // string for currency formatting
                      name: yup.string(),
                    }),
                  }),
                  clearance: clearanceSchema,
                })
              : yup.object({
                  beneficiary: yup.object({
                    type: yup.string(),
                    value: yup.object({
                      partyId: yup.string(),
                      partyTag: yup.string(),
                      reference: yup.string(),
                      amount: yup.string(), // string for currency formatting
                      vat: yup.boolean(),
                      name: yup.string(),
                    }),
                  }),
                  clearance: clearanceSchema,
                })
          }),
        )
        .test('paymentRules', 'Payment allocations exceed invoice amount, please adjust.', function (val) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'parent' does not exist on type 'Validate... Remove this comment to see the full error message
          const { parent } = this.options
          const netAmount = parseFloat(parent.netAmount)
          // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
          const paymentRulesTotal = val.reduce((acc, val) => {
            let amount = parseFloat(val?.beneficiary?.value?.amount)
            if (amount === undefined || isNaN(amount)) {
              amount = 0
            }
            return acc + amount
          }, 0)
          if (!netAmount && !paymentRulesTotal) {
            return true
          }
          if (netAmount && !paymentRulesTotal) {
            return true
          }
          return paymentRulesTotal <= netAmount
        }),
      description: yup.string(),
    }),
  ),
})

const InvoicesForm: React.FC<InvoicesFormWithFormikProps> = ({
  agencyId,
  ownerId,
  currentPortfolioId,
  submitForm,
  leaseType,
  monthlyInvoiceTypes,
  onceOffInvoiceTypes,
  invoiceParties,
  getInvoiceTypeNameByValue,
  defaultBeneficiaries,
  values,
  errors,
  handleInvoiceTemplateChanged,
  handleInvoiceTemplateRemoved,
  getPartyTagsById,
  partyTags,
  easyPayData,
  fetchEasyPayData,
  agencyName,
  agencyBankDetails,
  propertyOwnerName,
  propertyOwnerBankDetails,
  getPartyBankDetails,
  getPartyNameById,
  getInvoiceTypeByValue,
  isCurrentAgencyGlobalVatEnabled,
  // touched,
  dirty,
  isValid,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  validateForm,
  isDraftPortfolio,
  selectedTenant,
  redirect,
  isReadOnly,
  fetchInvoiceTypes,
  fetchPartyTags,
  emptyInvoice,
}) => {
  useEffect(() => {
    fetchInvoiceTypes?.()
    fetchPartyTags?.()
  }, [fetchInvoiceTypes, fetchPartyTags])

  // The administration fee can be charged once-off (via the "Rent & fees" step) or monthly, never both,
  // so hide it from the monthly type dropdown once a once-off administration fee template exists.
  const monthlyTypeOptions = useMemo(() => {
    const hasOnceOffAdministrationFee = values.invoices.some(
      (invoice: any) => invoice.category === 'AdministrationFee' && invoice.interval === 'OnceOff',
    )
    return hasOnceOffAdministrationFee
      ? monthlyInvoiceTypes.filter(type => type.value !== 'AdministrationFee')
      : monthlyInvoiceTypes
  }, [values.invoices, monthlyInvoiceTypes])

  const addInvoice = (push: any) => {
    push({ ...emptyInvoice, interval: 'BeginningOfMonth' })
  }

  const addOnceoffInvoice = (push: any) => {
    push({ ...emptyInvoice, interval: 'OnceOff', isEmpty: true })
  }

  const isCurrentInvoiceTemplateEmpty = () => {
    if (values.invoices.length === 0) {
      return false
    }

    const currentInvoice = values.invoices.filter((inv: any) => inv.editing)
    return currentInvoice[0] === emptyInvoice
  }

  return (
    <div className={styles.root}>
      <RouterPromptModal
        when={dirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSave={submitForm}
        onDiscard={() => {
          return true
        }}
      />
      {selectedTenant !== null ? (
        <Form className={styles.form}>
          <fieldset disabled={isReadOnly}>
            <div className={styles.body}>
              {!isDraftPortfolio && (
                <InfoBox type="info" className={styles['info-box']}>
                  <header>
                    <SvgInfo />
                    <p>
                      Changes to invoice templates will not affect invoices that have already been generated. Adding,
                      updating or removing a template will only take effect from the next generation date (the 10th).
                    </p>
                  </header>
                </InfoBox>
              )}
              <FieldArray
                name="invoices"
                render={arrayHelpers => {
                  const rentInvoices: any = []
                  const monthlyInvoices: any = []
                  const onceoffRentInvoices: any = []
                  const onceoffInvoices: any = []
                  values.invoices.forEach((invoice: any, index: number) => {
                    if (invoice.interval === 'OnceOff') {
                      // Unmanaged leases have no recurring invoices, so their rent invoice lands in
                      // this group — hoist it to the top the same way the monthly group does.
                      const target = invoice.category === 'RentInvoice' ? onceoffRentInvoices : onceoffInvoices
                      target.push(
                        <InvoicePanel
                          key={index}
                          agencyId={agencyId}
                          ownerId={ownerId}
                          invoiceTypeOptions={onceOffInvoiceTypes}
                          getInvoiceTypeNameByValue={getInvoiceTypeNameByValue}
                          invoiceParties={invoiceParties}
                          defaultBeneficiaries={defaultBeneficiaries}
                          invoice={invoice}
                          index={index}
                          handleInvoiceTemplateChanged={handleInvoiceTemplateChanged}
                          handleInvoiceTemplateRemoved={(id: any) => {
                            arrayHelpers.handleRemove(index)
                            id && handleInvoiceTemplateRemoved(id)
                          }}
                          getPartyTagsById={getPartyTagsById}
                          partyTags={partyTags}
                          // Formik helpers
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          validateForm={validateForm}
                          arrayHelpers={arrayHelpers}
                          errors={get(errors, `invoices[${index}]`, false)}
                          isSubmitting={isSubmitting}
                          easyPayData={easyPayData}
                          fetchEasyPayData={fetchEasyPayData}
                          agencyName={agencyName}
                          agencyBankDetails={agencyBankDetails}
                          getPartyBankDetails={getPartyBankDetails}
                          propertyOwnerId={ownerId}
                          propertyOwnerName={propertyOwnerName}
                          propertyOwnerBankDetails={propertyOwnerBankDetails}
                          portfolioId={currentPortfolioId}
                          getPartyNameById={getPartyNameById}
                          getInvoiceTypeByValue={getInvoiceTypeByValue}
                          isCurrentAgencyGlobalVatEnabled={isCurrentAgencyGlobalVatEnabled}
                        />,
                      )
                    } else if (leaseType !== leaseTypes.unmanaged) {
                      // The rent invoice is always rendered first, regardless of its position in the array
                      const target = invoice.category === 'RentInvoice' ? rentInvoices : monthlyInvoices
                      target.push(
                        <InvoicePanel
                          agencyId={agencyId}
                          ownerId={ownerId}
                          key={index}
                          invoiceTypeOptions={monthlyTypeOptions}
                          getInvoiceTypeNameByValue={getInvoiceTypeNameByValue}
                          invoiceParties={invoiceParties}
                          defaultBeneficiaries={defaultBeneficiaries}
                          invoice={invoice}
                          index={index}
                          handleInvoiceTemplateChanged={handleInvoiceTemplateChanged}
                          handleInvoiceTemplateRemoved={(id: any) => {
                            id && handleInvoiceTemplateRemoved(id)
                          }}
                          getPartyTagsById={getPartyTagsById}
                          partyTags={partyTags}
                          // Formik helpers
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          validateForm={validateForm}
                          arrayHelpers={arrayHelpers}
                          errors={get(errors, `invoices[${index}]`, false)}
                          isSubmitting={isSubmitting}
                          easyPayData={easyPayData}
                          fetchEasyPayData={fetchEasyPayData}
                          agencyName={agencyName}
                          agencyBankDetails={agencyBankDetails}
                          propertyOwnerId={ownerId}
                          propertyOwnerName={propertyOwnerName}
                          propertyOwnerBankDetails={propertyOwnerBankDetails}
                          getPartyBankDetails={getPartyBankDetails}
                          portfolioId={currentPortfolioId}
                          getPartyNameById={getPartyNameById}
                          getInvoiceTypeByValue={getInvoiceTypeByValue}
                          isCurrentAgencyGlobalVatEnabled={isCurrentAgencyGlobalVatEnabled}
                        />,
                      )
                    }
                  })
                  return (
                    <React.Fragment>
                      <Divider>
                        <div className={styles['divider-center-label']}>
                          <h2>
                            {leaseType === leaseTypes.unmanaged ? 'Once-off invoices' : 'Monthly recurring invoices'}
                          </h2>
                        </div>
                      </Divider>
                      {rentInvoices}
                      {monthlyInvoices}
                      {!isCurrentInvoiceTemplateEmpty() && leaseType !== leaseTypes.unmanaged && (
                        <Segment.Group
                          interactive
                          className={styles['add-invoice']}
                          onClick={() => addInvoice(arrayHelpers.push)}
                        >
                          <Segment>
                            <Grid>
                              <Grid.Column xs={12} md={11}>
                                <h4>Create monthly recurring invoice &#43;</h4>
                              </Grid.Column>
                            </Grid>
                          </Segment>
                        </Segment.Group>
                      )}
                      {isDraftPortfolio && (
                        <>
                          {leaseType !== leaseTypes.unmanaged && (
                            <Divider>
                              <div className={styles['divider-center-label']}>
                                <h2>Once-off invoices</h2>
                              </div>
                            </Divider>
                          )}
                          {onceoffRentInvoices}
                          {onceoffInvoices}
                          {!isCurrentInvoiceTemplateEmpty() && (
                            <Segment.Group
                              interactive
                              className={styles['add-invoice']}
                              onClick={() => addOnceoffInvoice(arrayHelpers.push)}
                            >
                              <Segment>
                                <Grid>
                                  <Grid.Column xs={12} md={11}>
                                    <h4>Create once-off invoice &#43;</h4>
                                  </Grid.Column>
                                </Grid>
                              </Segment>
                            </Segment.Group>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  )
                }}
              />
            </div>
            <div className={styles.footer}>
              {isDraftPortfolio ? (
                <>
                  <Button onClick={() => redirect?.(`/leases/${currentPortfolioId}/edit/commission`)}>Back</Button>
                  <Button loading={isSubmitting} type="submit">
                    Next
                  </Button>
                </>
              ) : (
                <Button loading={isSubmitting} disabled={!dirty} type="submit">
                  Save
                </Button>
              )}
            </div>
          </fieldset>
        </Form>
      ) : (
        <>
          <p>You need to add a tenant before you can configure invoices.</p>
          <Button onClick={() => redirect?.(`/leases/${currentPortfolioId}/edit/tenants`)}>Add tenant</Button>
        </>
      )}
    </div>
  )
}

export default withFormik<InvoicesFormProps, FormValues>({
  validationSchema,
  validateOnBlur: true,
  validateOnChange: true,
  enableReinitialize: true,
  mapPropsToValues: props => ({ invoices: [], ...props.initialValues }),
  handleSubmit: (values, { props }) => {
    props.onSubmit?.(values)
  },
})(InvoicesForm)
